import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Wand2 } from "lucide-react";
import { api } from "../lib/api";
import { useActiveBrand } from "../lib/brand";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { DraftsList } from "./DraftsList";
import {
  ImageSourceSelect,
  useImageSourceState,
} from "../components/ImageSourceSelect";
import { useToast } from "../components/Toast";

/** Local "YYYY-MM-DD" for a Date (date-input value). */
function toLocalYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Default target publish date — today + 7 (rolling D+7), editable. */
const DEFAULT_TARGET_DATE = toLocalYmd(new Date(Date.now() + 7 * 86_400_000));

/**
 * Custom mode: the operator types a free-text INSTRUCTION (+ optional English
 * image keywords + optional publish date) and AI generates a normal draft
 * (caption + image). The produced draft is identical to any other draft — full
 * edit/approve/schedule/publish/revise work via its detail page. Generated
 * drafts are listed below via the shared DraftsList in `custom` mode.
 */
export function Custom() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const accountKey = useActiveBrand();
  const [instruction, setInstruction] = useState("");
  const [imageQuery, setImageQuery] = useState("");
  const [targetDate, setTargetDate] = useState(DEFAULT_TARGET_DATE);
  const [imageSource, setImageSource] = useImageSourceState();

  const generate = useMutation({
    mutationFn: () => {
      if (!accountKey) throw new Error("Pilih brand terlebih dahulu");
      return api.generateCustom({
        accountKey,
        instruction: instruction.trim(),
        imageQuery: imageQuery.trim() || undefined,
        targetDate: targetDate || undefined,
        imageSource,
      });
    },
    onSuccess: () => {
      toast.success(
        "Konten custom sedang dibuat… (badge ‘Sedang generate’ akan muncul; hasil tampil otomatis)",
      );
      // The draft is produced asynchronously (claude + render); refetch the
      // drafts list a few times as it lands.
      [6000, 15000, 30000].forEach((ms) =>
        setTimeout(
          () => void queryClient.invalidateQueries({ queryKey: ["drafts"] }),
          ms,
        ),
      );
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Generation failed"),
  });

  const canGenerate =
    !!accountKey && instruction.trim().length > 0 && !generate.isPending;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-navy">
            Custom
          </h1>
          <p className="text-sm text-slate-500">
            Tulis instruksi bebas — AI membuat satu post (caption + gambar)
            sesuai permintaan Anda.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Buat konten custom</CardTitle>
          <CardDescription>
            Jelaskan post yang Anda mau. Kosongkan kata kunci foto agar AI
            memilih gambar yang relevan.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[10rem_1fr]">
            <div className="space-y-1.5">
              <Label htmlFor="target-date">Tanggal tayang</Label>
              <Input
                id="target-date"
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="instruction">Instruksi</Label>
            <Textarea
              id="instruction"
              rows={5}
              placeholder="Jelaskan post yang Anda mau: topik, sudut pandang, tujuan…"
              value={instruction}
              disabled={generate.isPending}
              onChange={(e) => setInstruction(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_14rem]">
            <div className="space-y-1.5">
              <Label htmlFor="image-query">
                Kata kunci foto (opsional, English)
              </Label>
              <Input
                id="image-query"
                placeholder="e.g. modern office, team collaborating"
                value={imageQuery}
                disabled={generate.isPending || imageSource === "library"}
                onChange={(e) => setImageQuery(e.target.value)}
              />
              <p className="text-xs text-slate-500">
                {imageSource === "library"
                  ? "Sumber gambar = Galeri: AI memilih gambar dari galeri Anda (kata kunci foto diabaikan)."
                  : "Kosongkan agar AI memilih foto stok Pexels yang relevan secara otomatis."}
              </p>
            </div>
            <ImageSourceSelect
              id="custom-image-source"
              value={imageSource}
              onChange={setImageSource}
              disabled={generate.isPending}
            />
          </div>

          <div>
            <Button
              variant="primary"
              onClick={() => generate.mutate()}
              disabled={!canGenerate}
            >
              {generate.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Wand2 className="h-4 w-4" />
              )}
              Generate
            </Button>
          </div>
        </CardContent>
      </Card>

      <DraftsList mode="custom" />
    </div>
  );
}
